import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

interface ScopeInfo {
  min_score: number;
  max_papers: number;
  out_of_scope_count: number;
  fallback_all: boolean;
}

export interface OutOfScopePaper {
  id: string;
  title: string;
  normalized_score: number;
}

/** Cap the rendered list — hundreds of rows in a dropdown is dead weight;
 * the tail is all low-score papers the footer summarizes. */
const MAX_ROWS = 100;

/** Papers excluded from stance assignment by relevance scoping — an honesty
 * bucket, deliberately distinct from UnalignedPanel's "No clear stance"
 * (those papers WERE assessed; these were not).
 *
 * `papers` is the client-derived list (current scores, score-descending —
 * see outOfScopePapers in useAnalyzeToolState). When provided, it also
 * drives the header count so the panel is internally consistent after
 * rescores; the map's snapshot count is the fallback (the shared route
 * passes no papers). */
export function OutOfScopePanel({
  scope,
  papers,
}: {
  scope: ScopeInfo;
  papers?: OutOfScopePaper[];
}) {
  const [open, setOpen] = useState(false);
  const count = papers ? papers.length : scope.out_of_scope_count;
  if (scope.fallback_all || count === 0) return null;
  const shown = papers?.slice(0, MAX_ROWS);
  const hidden = papers ? papers.length - (shown?.length ?? 0) : 0;
  return (
    <div className="w-[22rem] bg-background/95 border border-border rounded-lg shadow-lg pointer-events-auto">
      <div className="flex items-center px-3 py-2">
        <button
          type="button"
          onClick={() => setOpen((p) => !p)}
          className="flex items-center gap-1.5 text-muted-foreground text-xs font-semibold hover:text-foreground transition-colors"
        >
          {open ? (
            <ChevronDown className="w-3 h-3" />
          ) : (
            <ChevronRight className="w-3 h-3" />
          )}
          Out of scope ({count})
        </button>
      </div>
      {open && (
        <div className="px-3 pb-3 text-xs text-muted-foreground leading-snug">
          <p>
            These papers weren't assessed for a stance: relevance score below{" "}
            {scope.min_score}, or beyond the top {scope.max_papers} papers by
            score. Rescore the corpus or adjust a paper's score to include it.
          </p>
          {shown && shown.length > 0 && (
            <div className="mt-2 max-h-[40vh] overflow-y-auto min-h-0 space-y-1">
              {shown.map((p) => (
                <div key={p.id} className="flex items-start gap-2">
                  <span className="shrink-0 tabular-nums rounded bg-muted px-1 py-px text-[10px] font-semibold">
                    {p.normalized_score}
                  </span>
                  <span className="leading-snug">
                    {p.title || `Paper ${p.id}`}
                  </span>
                </div>
              ))}
              {hidden > 0 && (
                <div className="pt-1 text-[10px] opacity-60">
                  +{hidden} more below score{" "}
                  {shown[shown.length - 1].normalized_score}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
